import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppRoute } from 'app/app-layout/constants';
import { AppScopes } from 'app/core/authentication/models/scopes.enum';
import { AuthService } from 'app/core/authentication/services';
import { BrowserService } from 'app/core/browser/services/browser.service';
import { UserInterface } from 'app/core/user/models';
import { UserService } from 'app/core/user/services';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { UserProfileDialogComponent } from '../user-profile-dialog/user-profile-dialog.component';

export class NameLinkOptions {
  public showBadge?: boolean;
  public badgeOnRight?: boolean;
  public showLogout?: boolean;
  public showUserName?: boolean;
  public linkToDetails?: boolean;
  public linkToProfile?: boolean;
  constructor() { }
}

const defaultBadgeConfig: NameLinkOptions = {
  showBadge: false,
  badgeOnRight: false,
  showLogout: false,
  showUserName: true,
  linkToDetails: false,
  linkToProfile: true,
};

@Component({
  selector: 'cz-user-name-link',
  templateUrl: './user-name-link.component.html',
  styleUrls: ['./user-name-link.component.scss'],
})
export class UserNameLinkComponent implements OnInit, OnDestroy {
  @Input() public user: UserInterface;
  @Input() public config: NameLinkOptions;
  @Input() public displayLicence:boolean;
  public canViewUserDetails: boolean;

  public userName: string;
  public displayName: string;
  public userStatus: string;
  public deletedUser: boolean = false;

  public readonly logoutRoute: string = AppRoute.logout;

  private readonly _destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly _authService: AuthService,
    private readonly _userService: UserService,
    private readonly _browserService: BrowserService,
    private readonly _translate: TranslateService,
    private readonly router: Router,
    private dialog: MatDialog) {}

  public ngOnInit(): void {
    if(!this.config) {
      this.config = defaultBadgeConfig;
    }
    // Determine if the user can view user details.
    this._authService.isLoggedInUserAuthorized([AppScopes.USER_PROFILE])
      .pipe(take(1))
      .subscribe((isAuthorized: boolean) => this.canViewUserDetails = isAuthorized);

    // Handles the case where user might be system generated and therefore have no details available.
    if(this.canViewUserDetails) {
      this.canViewUserDetails = this.user.id && this.user.id > 0
                                && (this.config.linkToDetails || this.config.linkToProfile);
    } else {
      // handles rule that any user may view their own profile.
      this._authService.getAuthenticatedUser()
      .pipe(take(1))
      .subscribe((authorizedUser: UserInterface) => {
        this.canViewUserDetails = authorizedUser.id === this.user.id;
      });
    }

    if(!this.user || this.user.id < 1) {
      this.displayName = this._translate.instant('$message_deleted_user');
      this.userName = this._translate.instant('$common_n_a');
      this.userStatus = '$deleted_parenthesis';
      this.deletedUser = true;
    } else {
      this.displayName = this.user.displayName ? this.user.displayName : this.user.firstName + ' ' + this.user.lastName;
      this.userName = this.user.userName;
      this.userStatus = this.user.userDisabled ? this._translate.instant('$user_no_access') : '';
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  public viewUserDetails(): void {
    if (!this.user.id || !this.canViewUserDetails) { return; }

    if(this.config.linkToDetails) {
      this._userService.setSelectedUser(this.user.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((returnedUserId: number) => {
        if (!returnedUserId) { return; }

        // close any open dialogs
        this.dialog.closeAll();

        // navigate to user details
        this._browserService.toUserDetails();
      });
    } else {
      this.dialog.open(UserProfileDialogComponent, {
        height: '900px',
        width: '800px',
        panelClass: 'user-profile-dialog',
        data: this.user,
      });
    }
  }

  public logout() {
    this.router.navigateByUrl(this.logoutRoute);
  }
}
